import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { submitWarrantyQuote, uploadWarrantyContract } from "@/Calls/callWarrantyAuth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowLeft, Upload } from "lucide-react";

const COVERAGE_TYPES = [
    "POWERTRAIN",
    "BUMPER_TO_BUMPER",
    "DRIVETRAIN",
    "BASIC",
    "EXTENDED",
    "EMISSIONS",
    "ROADSIDE",
];

export default function WarrantySubmitQuotePage() {
    const { requestId } = useParams<{ requestId: string }>();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        coverage_type:  "POWERTRAIN",
        coverage_months: "",
        coverage_miles:  "",
        deductible:      "",
        monthly_cost:    "",
        total_cost:      "",
        notes:           "",
    });
    const [contractNumber, setContractNumber] = useState("");
    const [contractFile,   setContractFile]   = useState<File | null>(null);
    const [submitting,     setSubmitting]      = useState(false);
    const [error,          setError]           = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!requestId) return;
        setSubmitting(true);
        setError(null);
        try {
            // 1. Submit quote
            const res = await submitWarrantyQuote(Number(requestId), {
                coverage_type:  form.coverage_type,
                coverage_months: form.coverage_months ? Number(form.coverage_months) : undefined,
                coverage_miles:  form.coverage_miles  || undefined,
                deductible:      form.deductible       || undefined,
                monthly_cost:    form.monthly_cost     || undefined,
                total_cost:      form.total_cost       || undefined,
                notes:           form.notes            || undefined,
            });

            // 2. Optionally upload contract PDF
            if (res.data.ok && (contractFile || contractNumber)) {
                const fd = new FormData();
                if (contractNumber) fd.append("contract_number", contractNumber);
                if (contractFile)   fd.append("contract", contractFile);
                await uploadWarrantyContract(res.data.quote.quote_id, fd);
            }

            navigate("/warranty/my-quotes");
        } catch (e: any) {
            setError(e.response?.data?.message ?? "Failed to submit quote");
        } finally {
            setSubmitting(false);
        }
    };

    const field = (key: keyof typeof form) => ({
        value: form[key],
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
            setForm((f) => ({ ...f, [key]: e.target.value })),
    });

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <div className="flex items-center gap-3">
                <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                    <h1 className="text-xl font-semibold">Submit Warranty Quote</h1>
                    <p className="text-sm text-muted-foreground">Request #{requestId}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <Card className="p-5 space-y-4">
                    <h2 className="font-semibold">Coverage Details</h2>

                    <div>
                        <label className="text-sm font-medium">Coverage Type</label>
                        <select {...field("coverage_type")} className="mt-1 block w-full rounded-lg border bg-background px-3 py-2 text-sm">
                            {COVERAGE_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium">Coverage (months)</label>
                            <Input type="number" placeholder="e.g. 36" className="mt-1" {...field("coverage_months")} />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Coverage (miles)</label>
                            <Input placeholder="e.g. 36,000" className="mt-1" {...field("coverage_miles")} />
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm font-medium">Deductible</label>
                            <Input type="number" placeholder="100.00" className="mt-1" {...field("deductible")} />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Monthly Cost</label>
                            <Input type="number" placeholder="49.99" className="mt-1" {...field("monthly_cost")} />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Total Cost</label>
                            <Input type="number" placeholder="1799.64" className="mt-1" {...field("total_cost")} />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium">Notes</label>
                        <textarea
                            className="mt-1 block w-full rounded-lg border bg-background px-3 py-2 text-sm resize-none"
                            rows={3}
                            placeholder="Coverage exclusions, terms, conditions..."
                            {...field("notes")}
                        />
                    </div>
                </Card>

                <Card className="p-5 space-y-4">
                    <h2 className="font-semibold flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Contract / Binder (optional)
                    </h2>
                    <div>
                        <label className="text-sm font-medium">Contract Number</label>
                        <Input
                            placeholder="WC-2025-XXXXX"
                            className="mt-1"
                            value={contractNumber}
                            onChange={(e) => setContractNumber(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Upload PDF / Image</label>
                        <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="mt-1 block w-full text-sm text-muted-foreground file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border file:text-sm file:bg-slate-50 file:text-slate-700"
                            onChange={(e) => setContractFile(e.target.files?.[0] ?? null)}
                        />
                    </div>
                </Card>

                {error && <p className="text-sm text-red-500">{error}</p>}

                <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
                    <Button type="submit" className="bg-purple-600 text-white hover:bg-purple-700" disabled={submitting}>
                        {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Submit Quote
                    </Button>
                </div>
            </form>
        </div>
    );
}
